/**
 * Subjects Module — single source of truth for subjects across the app.
 * Storage key: 'studymeneer_subjects'
 *
 * ES module. Loaded via <script type="module"> in index.html, which means it is
 * deferred: it executes after every classic script, but before DOMContentLoaded.
 * Every consumer (app.js, notes.js, pdfLibrary.js, flashcards.js) reads it from
 * inside a function called on or after DOMContentLoaded, so the shim at the
 * bottom is in place before anything asks for it.
 */

const KEY = 'studymeneer_subjects';

/**
 * Every stored subject, archived ones included.
 *
 * This is what every MUTATION must read. getAll() hides archived subjects, so
 * building a new array from it — `getAll().push(x)` — would write the archived
 * ones out of existence. That is the whole reason the two are separate names
 * rather than a flag on one function.
 */
function getEvery() {
    try {
        const parsed = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
}

/**
 * The subjects a student is currently working with.
 *
 * Archiving exists so that finishing a term does not require deleting — which
 * orphans every record keyed to the subject, irreversibly. An archived subject
 * keeps all of its content and all of its links; it just stops appearing in
 * pickers, dropdowns, the dashboard and Meneer's rail.
 *
 * getById() deliberately still resolves archived subjects, so a note written
 * last term still shows its name and colour instead of going blank.
 */
function getAll() {
    return getEvery().filter(s => s && !s.archived);
}

function getArchived() {
    return getEvery().filter(s => s && s.archived);
}

// The single choke point for every subject mutation — create(), update() and
// delete() all land here — which is why the cloud push belongs here and not in
// each of them.
//
// Subjects are the spine every other record keys off (AGENTS.md §5), and until
// 2026-08-10 they had no push, no table and no pull at all: a second device
// received the notes, decks and quizzes and none of the subjects they reference,
// so the whole library arrived uncategorised. Fire-and-forget, like every other
// push — a sync failure must never fail the save.
function save(array) {
    localStorage.setItem(KEY, JSON.stringify(array));
    try { window.CloudSync?.push?.('subjects', array); } catch (_) {}
}

// Date.now() alone collides when two subjects are created in the same
// millisecond, and every subject then shares an id — so deleting one deletes
// the others, and getById()/update() silently act on the first match. A human
// clicking "add subject" will never hit it; Meneer's planned create_subject
// tool and any bulk import will hit it immediately. The random suffix keeps
// ids roughly time-ordered while making a collision practically impossible.
function newId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function create(name, colour) {
    const subjects = getEvery();
    const subject = {
        id: newId(),
        name,
        colour,
        createdAt: new Date().toISOString()
    };
    subjects.push(subject);
    save(subjects);
    return subject;
}

function update(id, name, colour) {
    const subjects = getEvery();
    const idx = subjects.findIndex(s => s.id === id);
    if (idx === -1) return false;
    subjects[idx].name   = name;
    subjects[idx].colour = colour;
    save(subjects);
    return true;
}

/**
 * Archive or restore.
 *
 * Reversible by construction, and the reason deleting should be rare: nothing
 * is removed and no id changes, so every task, note, deck, PDF and thread stays
 * attached and comes back intact on restore.
 */
function setArchived(id, archived) {
    const subjects = getEvery();
    const idx = subjects.findIndex(s => s.id === id);
    if (idx === -1) return false;
    if (archived) subjects[idx].archived = true;
    // Deleted rather than set to false: an absent flag is the same state, and
    // the record shape stays as small as it started.
    else delete subjects[idx].archived;
    save(subjects);
    return true;
}

function deleteSubject(id) {
    save(getEvery().filter(s => s.id !== id));
}

function getById(id) {
    return getEvery().find(s => s.id === id) || null;
}

/**
 * Narrow any collection to one subject.
 *
 * A falsy id means "all subjects" and returns the collection untouched, so a
 * caller can pass its filter state straight through without branching. Shared
 * rather than re-implemented per screen: the string comparison below is the
 * part that is easy to get subtly wrong, and it is the same question the hub,
 * the tasks list and the usage count all ask.
 */
function bySubject(records, subjectId) {
    const list = Array.isArray(records) ? records : [];
    if (subjectId === null || subjectId === undefined || subjectId === '') return list;
    return list.filter(r => r && String(r.subjectId) === String(subjectId));
}

/**
 * Re-point every record that names one subject at another.
 *
 * Pure, and returns a NEW array rather than mutating: the caller writes it
 * through the owning module's save path, so each collection's cloud push and
 * quota handling still run. Merge is the only way back from the duplicate
 * subjects a student ends up with after creating "Maths" twice on two devices.
 */
function reassign(records, fromId, toId) {
    if (!Array.isArray(records)) return { records: [], moved: 0 };

    let moved = 0;
    const out = records.map(r => {
        if (r && String(r.subjectId) === String(fromId)) {
            moved++;
            return { ...r, subjectId: toId };
        }
        return r;
    });

    return { records: out, moved };
}

// ── What a subject is carrying ────────────────────────────────────────────────
// Deleting a subject does NOT delete anything else — nothing cascades. What it
// does is orphan: every note, task, deck, PDF and timer session keeps a
// subjectId that no longer resolves, so the records survive while disappearing
// from the hub, the library filter, the flashcard bar and Meneer's rail. There
// is no way back, because recreating the subject mints a new id.
//
// That made subject deletion the only destructive action in this app with no
// confirmation (SKILL 2), on the one record type everything else keys off. The
// confirmation has to say what is attached, so this counts it.
//
// Pure on purpose: it takes the collections rather than reading storage, so the
// count is testable without a DOM and without a fake localStorage. Reading them
// is the caller's job.
const EMPTY_USAGE = Object.freeze({
    tasks: 0, notes: 0, decks: 0, files: 0, sessions: 0, conversation: 0, total: 0
});

function usage(subjectId, sources) {
    if (subjectId === null || subjectId === undefined || subjectId === '') return { ...EMPTY_USAGE };

    const src = sources || {};
    const list = v => (Array.isArray(v) ? v : []);
    // Compared as strings: ids are strings today, but a subjectId that came
    // back from a JSON round trip as a number must not silently count as zero.
    const mine = r => r && String(r.subjectId) === String(subjectId);

    const tasksSrc = src.tasks || {};
    const counts = {
        tasks:    list(tasksSrc.pending).filter(mine).length +
                  list(tasksSrc.completed).filter(mine).length,
        notes:    list(src.notes).filter(mine).length,
        decks:    list(src.decks).filter(mine).length,
        files:    list(src.files).filter(mine).length,
        sessions: list(src.sessions).filter(mine).length,
        // Conversations carry a subjectId under schema v2; their own id is
        // random, and a subject can hold many. Matching on `id` — which is what
        // v1 did, because a thread WAS its subject — counts ZERO after the
        // migration, and this number is the consequence line in an irreversible
        // delete dialog. Understating it is the one direction that matters.
        //
        // `!= null` keeps General out: it belongs to no subject, and counting it
        // would inflate every delete dialog by the whole General transcript.
        //
        // Taking the whole list and filtering here — rather than accepting a
        // pre-counted number — is deliberate: a scalar could not be checked
        // against the subject being asked about, and counted the same messages
        // for every subject.
        conversation: list(src.conversations)
            .filter(c => c && c.subjectId != null && String(c.subjectId) === String(subjectId))
            .reduce((n, c) => n + list(c.messages).length, 0),
    };
    counts.total = counts.tasks + counts.notes + counts.decks +
                   counts.files + counts.sessions + counts.conversation;
    return counts;
}

/**
 * The countable half of a delete confirmation, in plain words.
 * Returns '' when nothing is attached — the caller must not print "0 tasks".
 */
function usageSummary(counts) {
    const u = counts || EMPTY_USAGE;
    const parts = [];
    const add = (n, one, many) => { if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`); };

    add(u.tasks, 'task', 'tasks');
    add(u.notes, 'note', 'notes');
    add(u.decks, 'flashcard deck', 'flashcard decks');
    add(u.files, 'PDF', 'PDFs');
    add(u.sessions, 'study session', 'study sessions');
    add(u.conversation, 'Meneer message', 'Meneer messages');

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function getColour(id) {
    const s = getById(id);
    return s ? s.colour : 'var(--text-muted)';
}

export const Subjects = {
    getAll, getEvery, getArchived, save, create, update, setArchived,
    delete: deleteSubject, getById, getColour, usage, usageSummary,
    bySubject, reassign
};
export {
    getAll, getEvery, getArchived, save, create, update, setArchived,
    deleteSubject, getById, getColour, usage, usageSummary, bySubject, reassign
};

console.log('%c📚 Subjects module loaded', 'color: #7c6ff7; font-weight: bold');

// Transition shim (AGENTS.md SKILL 1). Classic scripts reach this through the
// global — app.js, notes.js, pdfLibrary.js and flashcards.js all use
// `window.Subjects`, and notes.js relies on the bare `Subjects` binding that a
// global property provides. Remove only once nothing references the global.
if (typeof window !== 'undefined') window.Subjects = Subjects;
